import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface PageMeta {
  title: string;
  description: string;
  keywords: string;
}

const PAGE_META: Record<string, PageMeta> = {
  '/': {
    title: 'Hogcloud | Websites, Mobile Apps, AI, Cloud, and 3D Printing',
    description:
      'Hogcloud builds static websites, custom websites, mobile apps, SMS scheduling tools, AI chatbots, cloud solutions, and custom 3D prints in Northwest Arkansas.',
    keywords:
      'Hogcloud, Fayetteville Arkansas web design, Northwest Arkansas website development, custom mobile apps, 3D printing, cloud consulting, AI chatbots, SMS scheduling',
  },
  '/services': {
    title: 'Hogcloud Services | Websites, Apps, Cloud Consulting, AI, 3D Printing',
    description:
      'Explore Hogcloud services for static websites, e-commerce, customer logins, mobile apps, SMS messaging, scheduling, cloud consulting, AI automation, and custom 3D printing.',
    keywords:
      'website services, static website, e-commerce integration, customer login, app development, SMS messaging, scheduling software, cloud consulting, AI automation, custom 3D printing',
  },
  '/services/websites': {
    title: 'Website Services | Static Sites, E-Commerce, SMS, SEO, Chatbots',
    description:
      'Hogcloud creates static websites, SEO-friendly business sites, customer logins, e-commerce and payment integrations, SMS messaging, scheduling, and AI chatbots.',
    keywords:
      'static website, SEO website, web design Fayetteville AR, business website, customer login website, e-commerce integration, payment provider integration, SMS messaging, scheduling, friendly chatbot, AI chatbot',
  },
  '/services/appdev': {
    title: 'Mobile App Development | Customer Logins, SMS, Scheduling, Ordering',
    description:
      'Custom mobile app development from simple business apps to customer logins, SMS messaging, scheduling, ordering, service requests, and cloud-powered growth.',
    keywords:
      'custom mobile app development, small business app, customer login app, SMS messaging app, scheduling app, ordering app, service request app, cloud mobile app',
  },
  '/services/consulting': {
    title: 'Tech Consulting | Cloud, AI Automation, SMS, Email, Business IT',
    description:
      'Hogcloud offers cloud consulting, AI and agentic automation training, AWS architecture guidance, email, SMS, invoicing, and practical business IT support.',
    keywords:
      'tech consulting, cloud consulting, AWS cloud architecture, AI automation training, agentic AI, business automation, SMS services, email services, invoicing software, IT consulting',
  },
  '/services/3dprints': {
    title: 'Custom 3D Printing | Magnets, Lithophanes, Keychains, Party Favors',
    description:
      'Custom 3D printed business magnets, license plates, lithophanes, keychains, character creatures, fan pendants, card trays, party favors, badges, and fidgets.',
    keywords:
      'custom 3D printing, business magnets, 3D printed license plates, lithophane, custom keychains, fan pendants, party favors, name badges, card trays, fidgets',
  },
  '/contact': {
    title: 'Contact Hogcloud | Websites, Apps, Consulting, and 3D Printing',
    description:
      'Contact Hogcloud for static websites, custom websites, mobile apps, SMS scheduling tools, cloud consulting, AI automation, and custom 3D printing projects.',
    keywords:
      'contact Hogcloud, web design quote, mobile app quote, 3D printing quote, cloud consulting, AI automation, Fayetteville Arkansas',
  },
  '/shop': {
    title: 'Custom 3D Printed Products | Hogcloud',
    description:
      'Shop Hogcloud custom 3D printed gifts, magnets, sports pendants, keychains, decorations, party favors, and personalized product ideas.',
    keywords:
      'shop custom 3D prints, 3D printed gifts, custom magnets, sports pendants, keychains, party favors, personalized 3D printing',
  },
  '/shop/snapcharms': {
    title: 'SnapCharms | Custom 3D Printed Character Charms',
    description:
      'Shop Hogcloud SnapCharms, cute custom 3D printed character charms for keychains, backpacks, necklaces, and pendants.',
    keywords:
      'SnapCharms, 3D printed charms, character keychains, backpack charms, custom pendants, Hogcloud',
  },
  '/shop/sports': {
    title: 'Custom Sports 3D Prints | Jerseys, Pendants, Keychains, Magnets',
    description:
      'Customize sports-themed 3D printed jerseys, pendants, keychains, magnets, emblems, footballs, basketball designs, and fan gifts.',
    keywords:
      'custom sports keychains, 3D printed sports pendants, jersey magnets, football keychains, basketball jersey pendant, fan gifts',
  },
  '/shop/locals': {
    title: 'Local 3D Printed Gifts | Street Signs and City Limits Magnets',
    description:
      'Shop custom local 3D printed street sign magnets, city limits signs, Fayetteville Arkansas gifts, and personalized local keepsakes.',
    keywords:
      'local 3D printed gifts, street sign magnets, city limits magnets, Fayetteville Arkansas gifts, custom local keepsakes',
  },
  '/about': {
    title: 'About Hogcloud | Creative Technology and Custom 3D Printing',
    description:
      'Learn about Hogcloud, a Northwest Arkansas business offering creative technology services, websites, apps, consulting, and custom 3D printing.',
    keywords:
      'about Hogcloud, Northwest Arkansas technology business, creative technology, custom websites, app development, 3D printing',
  },
};

const setMetaContent = (selector: string, content: string) => {
  const element = document.head.querySelector<HTMLMetaElement>(selector);
  if (element) {
    element.content = content;
  }
};

const SEO = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const pageMeta = PAGE_META[pathname] || PAGE_META['/'];
    const canonicalUrl = `https://hogcloud.net${pathname === '/' ? '/' : pathname}`;

    document.title = pageMeta.title;
    setMetaContent('meta[name="description"]', pageMeta.description);
    setMetaContent('meta[name="keywords"]', pageMeta.keywords);
    setMetaContent('meta[property="og:title"]', pageMeta.title);
    setMetaContent('meta[property="og:description"]', pageMeta.description);
    setMetaContent('meta[property="og:url"]', canonicalUrl);
    setMetaContent('meta[name="twitter:title"]', pageMeta.title);
    setMetaContent('meta[name="twitter:description"]', pageMeta.description);

    const canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) {
      canonical.href = canonicalUrl;
    }
  }, [pathname]);

  return null;
};

export default SEO;
